import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { PageHeader, useCodeLabel, useFormats } from '@/pages/bacen/shared';
import { bacenConsistencyParams, fetchBacenConsistency } from '@/services/bacen';
import type { BacenDivergentPair } from '@/services/types';

const PAGE_SIZE = 50;

/**
 * Pairs of ledger rows that share a basis and ended differently: co-accused
 * in one PAS, rows decided by one office for one penalty type in one year,
 * and rows whose CRSFN dispositions cite the same provision. Same-infraction
 * pairs on the ledger itself are refused: the ledger carries no infraction
 * field, and the refusal says how many rows carry a parsed provision.
 */
export function BacenConsistencyPage() {
  const { t } = useTranslation(['bacen', 'common']);
  const { number, currency, percent } = useFormats();
  const label = useCodeLabel();
  const [seed] = useSearchParams();
  const [basis, setBasis] = useState(seed.get('basis') ?? '');
  const [key, setKey] = useState(seed.get('key') ?? '');
  const [offset, setOffset] = useState(0);
  const filters = { basis: basis || null, key: key || null };

  const { data, isPending, error, refetch, isPlaceholderData } = useQuery({
    queryKey: ['bacen', 'consistency', filters, offset],
    queryFn: () => fetchBacenConsistency({ ...filters, limit: PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        page="bacen:consistency"
        exportPath={`/api/v1/bacen/export/consistency?${bacenConsistencyParams(filters).toString()}`}
      />

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-reach>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:consistency.reach.title')}
        </h2>
        <Answer answer={data.reach} help={t('bacen:consistency.reach.help')}>
          {(funnel) => (
            <div className="space-y-2">
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">
                        {t('bacen:consistency.reach.basis')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:consistency.reach.pairs')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:consistency.reach.bothDecided')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:consistency.reach.divergent')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:consistency.reach.rowsInPairs')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:consistency.reach.rowsDivergent')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {funnel.bases.map((b) => (
                      <tr key={b.basis} className="border-t" data-basis={b.basis}>
                        <td className="px-3 py-2 text-foreground">
                          <button
                            type="button"
                            onClick={() => {
                              setBasis(b.basis);
                              setOffset(0);
                            }}
                            className="text-primary underline-offset-2 hover:underline"
                          >
                            {label('basis', b.basis)}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(b.pairs)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(b.pairs_both_decided)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(b.pairs_divergent)}
                          <span className="ml-1 text-xs text-muted-foreground">
                            ({percent.format(b.divergent_share_of_decided)})
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(b.rows_in_pairs)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(b.rows_in_divergent_pairs)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('bacen:consistency.reach.summary', {
                  total: number.format(funnel.total_rows),
                  withProvision: number.format(funnel.rows_with_provision),
                })}
              </p>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="bacen:consistency.guide.reach" />
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <RefusalCard
          title={t('bacen:consistency.sameInfraction.title')}
          answer={data.same_infraction}
        />
        <section className="rounded-lg border bg-card p-4" data-by-group>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('bacen:consistency.groups.title')}
          </h2>
          <Answer answer={data.by_group} help={t('bacen:consistency.groups.help')}>
            {(groups) => (
              <ul className="max-h-72 divide-y overflow-auto rounded-md border text-sm">
                {groups.map((g) => (
                  <li
                    key={`${g.basis}-${g.key}`}
                    className="flex items-center justify-between gap-3 px-3 py-1.5"
                    data-group={g.key}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setBasis(g.basis);
                        setKey(g.key);
                        setOffset(0);
                      }}
                      className="truncate text-left text-primary underline-offset-2 hover:underline"
                    >
                      <span className="text-xs text-muted-foreground">
                        {label('basis', g.basis)}
                      </span>{' '}
                      {g.key}
                    </button>
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                      {number.format(g.divergent)} / {number.format(g.pairs)}
                      <span className="ml-1 text-xs">({percent.format(g.divergent_share)})</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Answer>
        </section>
      </div>

      <section
        className="rounded-lg border bg-card"
        data-pairs
        data-stale={isPlaceholderData || undefined}
      >
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-xs text-muted-foreground">
          <span data-total={data.total_pairs}>
            {t('bacen:consistency.pairs.count', {
              n: number.format(data.total_pairs),
            })}
            {basis ? ` · ${label('basis', basis)}` : null}
            {key ? ` · ${key}` : null}
          </span>
          {basis || key ? (
            <button
              type="button"
              onClick={() => {
                setBasis('');
                setKey('');
                setOffset(0);
              }}
              className="rounded border px-2 py-0.5 text-foreground"
            >
              {t('bacen:cases.filters.clear')}
            </button>
          ) : null}
        </div>
        {data.pairs.length === 0 ? (
          <div className="p-4">
            <EmptyState hint={t('bacen:consistency.pairs.empty')} />
          </div>
        ) : (
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('bacen:consistency.pairs.basis')}</th>
                  <th className="px-3 py-2 font-medium">{t('bacen:consistency.pairs.sideA')}</th>
                  <th className="px-3 py-2 font-medium">{t('bacen:consistency.pairs.sideB')}</th>
                </tr>
              </thead>
              <tbody>
                {data.pairs.map((p, i) => (
                  <tr key={i} className="border-t align-top">
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {label('basis', p.basis)}
                      <div className="font-mono">{p.key}</div>
                    </td>
                    <td className="px-3 py-2">
                      <PairSide pair={p} side="a" currency={currency} />
                    </td>
                    <td className="px-3 py-2">
                      <PairSide pair={p} side="b" currency={currency} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.total_pairs > PAGE_SIZE ? (
          <div className="flex items-center justify-between border-t px-4 py-2 text-xs">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              className="text-primary underline-offset-2 hover:underline disabled:text-muted-foreground"
            >
              {t('bacen:consistency.pairs.previous')}
            </button>
            <span className="text-muted-foreground">
              {t('bacen:consistency.pairs.page', {
                from: number.format(offset + 1),
                to: number.format(Math.min(offset + PAGE_SIZE, data.total_pairs)),
              })}
            </span>
            <button
              type="button"
              disabled={offset + PAGE_SIZE >= data.total_pairs}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="text-primary underline-offset-2 hover:underline disabled:text-muted-foreground"
            >
              {t('bacen:consistency.pairs.next')}
            </button>
          </div>
        ) : null}
      </section>
      <FieldGuide i18nKey="bacen:consistency.guide.pairs" />
    </div>
  );
}

function PairSide({
  pair,
  side,
  currency,
}: {
  pair: BacenDivergentPair;
  side: 'a' | 'b';
  currency: Intl.NumberFormat;
}) {
  const { t } = useTranslation(['bacen']);
  const label = useCodeLabel();
  const pas = side === 'a' ? pair.pas_a : pair.pas_b;
  const subject = side === 'a' ? pair.subject_a : pair.subject_b;
  const office = side === 'a' ? pair.office_a : pair.office_b;
  const year = side === 'a' ? pair.year_a : pair.year_b;
  const first = side === 'a' ? pair.first_code_a : pair.first_code_b;
  const final = side === 'a' ? pair.final_code_a : pair.final_code_b;
  const firstFine = side === 'a' ? pair.first_fine_a : pair.first_fine_b;
  const finalFine = side === 'a' ? pair.final_fine_a : pair.final_fine_b;
  const trajectory = side === 'a' ? pair.trajectory_a : pair.trajectory_b;
  return (
    <div className="space-y-0.5">
      <Link
        to={`/bacen/c/${pas}`}
        className="font-medium tabular-nums text-primary underline-offset-2 hover:underline"
      >
        {pas}
      </Link>
      <div className="line-clamp-1 text-foreground">{subject}</div>
      <div className="text-xs text-muted-foreground">
        {office ?? '—'} · {year ?? '—'}
      </div>
      <div className="text-xs text-muted-foreground">
        {label('penalty', first)}
        {firstFine !== null ? ` ${currency.format(firstFine)}` : null}
        {' → '}
        <span className="text-foreground">{label('penalty', final)}</span>
        {finalFine !== null ? ` ${currency.format(finalFine)}` : null}
        {trajectory ? ` · ${label('trajectory', trajectory)}` : null}
      </div>
      <span className="sr-only">{t('bacen:consistency.pairs.side', { side })}</span>
    </div>
  );
}
